import React from "react";

/** Shared shells so every wizard step looks the same (ported from portal). */

// "ready_to_start" -> "Ready To Start" — local equivalent of the portal's
// formatStatus helper.
export const formatStatus = (status) => {
  if (!status) return "-";
  return String(status)
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Status → coloured dot. The label stays grey: the dot carries the meaning,
// so a column of these reads as data rather than as a wall of coloured chips.
const STATUS_DOTS = {
  pending: "#FBBF24",
  in_progress: "#3B82F6",
  ongoing: "#3B82F6",
  completed: "#22C55E",
  ready_to_start: "#6366F1",
  paid: "#22C55E",
  delayed: "#EF4444",
};

export const StatusPill = ({ status }) => {
  const key = String(status || "")
    .toLowerCase()
    .replace(/\s+/g, "_");

  // "Created" is the default state everything starts in — it carries no
  // information for the reader, so the cell stays empty rather than
  // filling the column with a word that never varies.
  if (key === "created") {
    return <span style={{ fontSize: "14px", color: "#6B7280" }}>-</span>;
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "14px",
        color: "#6B7280",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: STATUS_DOTS[key] || "#9CA3AF",
        }}
      />
      {formatStatus(status)}
    </span>
  );
};

/** Neutral count chip — "4 activities", "2 attached". */
export const CountPill = ({ children, tone = "gray" }) => {
  const tones = {
    gray: { backgroundColor: "#F3F4F6", color: "#4B5563" },
    green: { backgroundColor: "#F0FDF4", color: "#15803D" },
    amber: { backgroundColor: "#FFFBEB", color: "#B45309" },
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 500,
        whiteSpace: "nowrap",
        ...(tones[tone] || tones.gray),
      }}
    >
      {children}
    </span>
  );
};

/**
 * Chronological order, undated rows last.
 *
 * Deliberately fed from server values rather than the live drafts on the steps
 * where dates are typed: re-sorting on every keystroke makes the row you are
 * editing jump out from under the cursor. Tables of locked, derived dates can
 * sort on what is displayed, since there is nothing to lose focus on.
 */
export const byDate = (getDate) => (a, b) => {
  const left = getDate(a);
  const right = getDate(b);
  if (left === right) return (a.id ?? 0) - (b.id ?? 0);
  if (!left) return 1;
  if (!right) return -1;
  return left < right ? -1 : 1;
};

// Fallbacks for a payout whose billing milestone is not in this scope's list.
const REFERENCE_TYPE_LABELS = {
  PROJECT_INITIAL_DEPOSIT: "Initial Deposit",
  PROJECT_COMPLETION: "Handover",
  PROJECT_WARRANTY_COMPLETION: "Warranty",
  DESIGN_INITIAL_DEPOSIT: "Initial Deposit",
  DESIGN_COMPLETION: "Handover",
  RENO_COMMISSION: "Reno Commission",
  PAYMENT_GROUP: "Payout",
};

/**
 * A payout is a copy of its billing milestone, so it should read like one.
 * The raw reference_type gives no hint that "PAYMENT_GROUP" is the
 * Mobilisation payout, so the milestone's own name wins whenever known.
 */
export const payoutLabel = (payout, groupName) =>
  groupName ||
  REFERENCE_TYPE_LABELS[String(payout?.reference_type || "").toUpperCase()] ||
  payout?.reference_type ||
  "Payout";

/**
 * What a customer payment is called. `display_name` is the label chosen when
 * the payment plan was set up and is the only field that tells the
 * INSTALLMENT rows apart. Falls back to the type so the row still reads.
 */
export const paymentName = (line, formatType) =>
  line?.name || line?.display_name || formatType(line?.type) || "Payment";

// No bottom margin — the parent's flex `gap` owns the spacing, so a heading
// sits the same distance from its table on every step.
export const StepHeader = ({ title, description, right }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "16px",
      flexWrap: "wrap",
    }}
  >
    <div>
      <h2
        style={{
          fontSize: "18px",
          fontWeight: 500,
          color: "#0d0d0e",
          margin: 0,
        }}
      >
        {title}
      </h2>
      {description && (
        <p
          style={{
            fontSize: "14px",
            color: "#4B5563",
            marginTop: "2px",
            marginBottom: 0,
          }}
        >
          {description}
        </p>
      )}
    </div>
    {right}
  </div>
);

export const Panel = ({ children, style = {} }) => (
  <div
    style={{
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      backgroundColor: "#fff",
      overflow: "hidden",
      ...style,
    }}
  >
    {children}
  </div>
);

/**
 * `widths` pins the columns instead of letting the content size them. Two
 * tables stacked in one section otherwise land on different column positions
 * and read as though they were unrelated.
 */
export const Table = ({ columns, widths, children }) => (
  <div style={{ overflowX: "auto" }}>
    <table
      style={{
        width: "100%",
        fontSize: "14px",
        borderCollapse: "collapse",
        tableLayout: widths ? "fixed" : "auto",
      }}
    >
      {widths && (
        <colgroup>
          {widths.map((width, index) => (
            <col key={columns[index] ?? index} style={{ width }} />
          ))}
        </colgroup>
      )}
      <thead>
        <tr style={{ borderBottom: "1px solid #F3F4F6" }}>
          {columns.map((column) => (
            <th
              key={column}
              style={{
                textAlign: "left",
                fontWeight: 400,
                fontSize: "14px",
                color: "#949daa",
                padding: "10px 16px",
              }}
            >
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

// Row border shared by step tables — tbody rows get a faint divider like the
// portal's `divide-y divide-gray-50`.
export const rowStyle = { borderTop: "1px solid #F9FAFB" };

export const Empty = ({ children }) => (
  <p
    style={{
      padding: "32px 16px",
      fontSize: "14px",
      color: "#6B7280",
      textAlign: "center",
      margin: 0,
    }}
  >
    {children}
  </p>
);

export const Notice = ({ tone = "info", children }) => {
  const tones = {
    info: {
      border: "1px solid #E5E7EB",
      backgroundColor: "#F9FAFB",
      color: "#374151",
    },
    warning: {
      border: "1px solid #FDE68A",
      backgroundColor: "#FFFBEB",
      color: "#78350F",
    },
    error: {
      border: "1px solid #FECACA",
      backgroundColor: "#FEF2F2",
      color: "#991B1B",
    },
  };
  return (
    <div
      style={{
        borderRadius: "12px",
        padding: "12px 16px",
        fontSize: "14px",
        ...(tones[tone] || tones.info),
      }}
    >
      {children}
    </div>
  );
};
